/**
 * IndexedDB cache for session/catalog views (web-client.md §3/§4). CACHE ONLY — server always wins;
 * on cold start the UI can render the cached view read-only ("reconnecting…") until sys.welcome
 * replaces it. Never stores tokens.
 *
 * Feature-detected: when IndexedDB is unavailable (Node, private-mode, disabled) every method
 * degrades to a no-op so the client still runs, just without warm-start caching.
 */
export interface SessionCache {
  put(key: string, value: unknown): Promise<void>;
  get<T>(key: string): Promise<T | null>;
  remove(key: string): Promise<void>;
  clear(): Promise<void>;
}

/** A cache that stores nothing (used when IndexedDB is absent). */
export class NoopSessionCache implements SessionCache {
  async put(): Promise<void> {}
  async get<T>(): Promise<T | null> {
    return null;
  }
  async remove(): Promise<void> {}
  async clear(): Promise<void> {}
}

/**
 * An in-memory {@link SessionCache}. Backs tests (a fake IndexedDB seam) and can serve as a
 * process-lifetime fallback. Values are structured-cloned via JSON so callers cannot mutate a stored
 * object by reference — matching the real IndexedDB round-trip.
 */
export class MemorySessionCache implements SessionCache {
  private readonly map = new Map<string, string>();

  async put(key: string, value: unknown): Promise<void> {
    this.map.set(key, JSON.stringify(value));
  }
  async get<T>(key: string): Promise<T | null> {
    const raw = this.map.get(key);
    return raw === undefined ? null : (JSON.parse(raw) as T);
  }
  async remove(key: string): Promise<void> {
    this.map.delete(key);
  }
  async clear(): Promise<void> {
    this.map.clear();
  }
}

const DB_NAME = 'team-simulator';
const STORE = 'sessions';

/** IndexedDB-backed cache. Construct via {@link openSessionCache} which feature-detects. */
class IndexedDbSessionCache implements SessionCache {
  constructor(private readonly db: IDBDatabase) {}

  put(key: string, value: unknown): Promise<void> {
    return this.tx('readwrite', (store) => store.put(value as never, key));
  }

  get<T>(key: string): Promise<T | null> {
    return new Promise((resolve, reject) => {
      const req = this.db.transaction(STORE, 'readonly').objectStore(STORE).get(key);
      req.onsuccess = () => resolve((req.result as T) ?? null);
      req.onerror = () => reject(req.error);
    });
  }

  remove(key: string): Promise<void> {
    return this.tx('readwrite', (store) => store.delete(key));
  }

  clear(): Promise<void> {
    return this.tx('readwrite', (store) => store.clear());
  }

  private tx(mode: IDBTransactionMode, op: (store: IDBObjectStore) => IDBRequest): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(STORE, mode);
      op(transaction.objectStore(STORE));
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }
}

/** Open the cache, or fall back to a no-op when IndexedDB is unavailable. */
export async function openSessionCache(): Promise<SessionCache> {
  const idb = (globalThis as { indexedDB?: IDBFactory }).indexedDB;
  if (!idb) return new NoopSessionCache();
  try {
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      const req = idb.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return new IndexedDbSessionCache(db);
  } catch {
    return new NoopSessionCache();
  }
}
